import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ProjectState, formatStageState } from '../../../dtos/project-state';
import {
  AgreementThresholdStatus,
  agreementInterpretationLabel,
  agreementThresholdStatusLabel,
} from '../../../dtos/screening-agreement';
import {
  DataExtractionAgreementPair,
  DataExtractionAgreementQuestion,
  DataExtractionAgreementResult,
  dataExtractionMetricLabel,
  dataExtractionQuestionTypeLabel,
  dataExtractionReasonLabel,
} from '../../../dtos/data-extraction-agreement';
import { ExtractionQuestion } from '../../../extraction-schema/domain/extraction-question.model';
import { DataExtractionAgreementService } from '../../../services/data-extraction-agreement.service';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

@Component({
  selector: 'app-data-extraction-agreement-card',
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
  templateUrl: './data-extraction-agreement-card.component.html',
})
export class DataExtractionAgreementCardComponent implements OnChanges {
  @Input() projectId = '';
  @Input() visible = false;
  @Input() projectState: ProjectState | string | null = null;
  @Input() preferOfficial = false;
  @Input() questions: ExtractionQuestion[] = [];

  loading = false;
  errorMessage = '';
  agreement: DataExtractionAgreementResult | null = null;
  detailsOpen = false;
  private expandedQuestionIds = new Set<number>();

  constructor(
    private agreementService: DataExtractionAgreementService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['projectId'] ||
      changes['visible'] ||
      changes['projectState'] ||
      changes['preferOfficial']
    ) {
      this.loadIfReady();
    }
  }

  retry(): void {
    this.loadIfReady(true);
  }

  get isOfficial(): boolean {
    return this.agreement?.calculationType === 'OFFICIAL';
  }

  get finalizedAt(): string {
    const agreement = this.agreement;
    if (!agreement || agreement.calculationType !== 'OFFICIAL') {
      return '';
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(agreement.finalizedAt));
  }

  get finalizedByUserId(): number | null {
    const agreement = this.agreement;
    return agreement?.calculationType === 'OFFICIAL' ? agreement.finalizedByUserId : null;
  }

  get finalizedByUserName(): string {
    const agreement = this.agreement;
    if (!agreement || agreement.calculationType !== 'OFFICIAL') {
      return '';
    }
    return agreement.finalizedByUserName || `User #${agreement.finalizedByUserId}`;
  }

  get categoricalQuestionCount(): number {
    return this.agreement?.questions.filter((question) => this.isCategorical(question)).length ?? 0;
  }

  get questionsRequiringReview(): number {
    return (
      this.agreement?.questions.filter(
        (question) =>
          question.unpairedMissingCount > 0 ||
          question.thresholdStatus === 'BELOW_THRESHOLD' ||
          question.thresholdStatus === 'NOT_COMPUTABLE',
      ).length ?? 0
    );
  }

  stageLabel(): string {
    return formatStageState(ProjectState.DATA_EXTRACTION);
  }

  questionLabel(question: DataExtractionAgreementQuestion): string {
    return (
      question.questionText?.trim() ||
      (this.questions.find((candidate) => candidate.id === question.questionId)?.text ??
        `Question ${question.questionId}`)
    );
  }

  questionTypeLabel(question: DataExtractionAgreementQuestion): string {
    return dataExtractionQuestionTypeLabel(question.questionType);
  }

  metricLabel(question: DataExtractionAgreementQuestion): string {
    return dataExtractionMetricLabel(question.metricType);
  }

  reasonLabel(question: DataExtractionAgreementQuestion): string {
    return dataExtractionReasonLabel(question.nonComputableReason);
  }

  interpretationLabel(value = this.agreement?.questions[0]?.interpretation): string {
    return agreementInterpretationLabel(value);
  }

  thresholdStatusLabel(value?: AgreementThresholdStatus | null): string {
    return agreementThresholdStatusLabel(value);
  }

  formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'Not available';
    }
    return `${value.toFixed(1)}%`;
  }

  formatNumber(value: number | null | undefined, digits = 2): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'Not available';
    }
    return value.toFixed(digits);
  }

  formatKappa(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'Not computable';
    }
    return value.toFixed(2);
  }

  resultSummary(question: DataExtractionAgreementQuestion): string {
    switch (question.metricType) {
      case 'UNWEIGHTED_COHEN_KAPPA':
        return `${this.formatKappa(question.cohenKappa)} kappa, ${this.formatPercent(
          question.percentAgreement,
        )} agreement`;
      case 'NUMERIC_DIFFERENCE':
        return `${this.formatPercent(question.percentAgreement)} exact, mean ${this.formatNumber(
          question.meanAbsoluteDifference,
        )}, median ${this.formatNumber(question.medianAbsoluteDifference)}`;
      default:
        return this.formatPercent(question.percentAgreement);
    }
  }

  questionStatus(question: DataExtractionAgreementQuestion): string {
    const reason = this.reasonLabel(question);
    if (reason) {
      return reason;
    }
    if (question.interpretation) {
      return this.interpretationLabel(question.interpretation);
    }
    return 'Question-specific metric';
  }

  isCategorical(question: DataExtractionAgreementQuestion): boolean {
    return question.metricType === 'UNWEIGHTED_COHEN_KAPPA';
  }

  isExpanded(question: DataExtractionAgreementQuestion): boolean {
    return this.expandedQuestionIds.has(question.questionId);
  }

  toggleQuestion(question: DataExtractionAgreementQuestion): void {
    if (!this.isCategorical(question) || question.reviewerPairs.length === 0) {
      return;
    }
    if (this.expandedQuestionIds.has(question.questionId)) {
      this.expandedQuestionIds.delete(question.questionId);
    } else {
      this.expandedQuestionIds.add(question.questionId);
    }
    this.expandedQuestionIds = new Set(this.expandedQuestionIds);
  }

  reviewerPairLabel(pair: DataExtractionAgreementPair): string {
    const first = pair.firstReviewerName || `User #${pair.firstReviewerId}`;
    const second = pair.secondReviewerName || `User #${pair.secondReviewerId}`;
    return `${first} / ${second}`;
  }

  trackQuestion(_: number, question: DataExtractionAgreementQuestion): number {
    return question.questionId;
  }

  trackPair(_: number, pair: DataExtractionAgreementPair): string {
    return `${pair.firstReviewerId}-${pair.secondReviewerId}`;
  }

  private loadIfReady(force = false): void {
    if (!this.visible || !this.projectId) {
      this.agreement = null;
      this.errorMessage = '';
      this.loading = false;
      this.detailsOpen = false;
      this.markForCheck();
      return;
    }

    if (this.loading && !force) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.agreement = null;
    this.expandedQuestionIds = new Set();
    this.markForCheck();

    if (this.shouldRequestPreviewFirst()) {
      this.loadPreview();
    } else {
      this.loadOfficialWithActiveStageFallback();
    }
  }

  private loadPreview(): void {
    this.agreementService
      .getPreview(this.projectId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.markForCheck();
        }),
      )
      .subscribe({
        next: (result) => {
          this.agreement = result;
          this.markForCheck();
        },
        error: (error) => this.handleError(error),
      });
  }

  private loadOfficialWithActiveStageFallback(): void {
    this.agreementService.getOfficial(this.projectId).subscribe({
      next: (result) => {
        this.agreement = result;
        this.loading = false;
        this.markForCheck();
      },
      error: (error) => {
        if (this.isNotFound(error) && this.projectState === ProjectState.DATA_EXTRACTION) {
          this.loadPreview();
          return;
        }
        this.loading = false;
        this.handleError(error);
        this.markForCheck();
      },
    });
  }

  private shouldRequestPreviewFirst(): boolean {
    return !this.preferOfficial && this.projectState === ProjectState.DATA_EXTRACTION;
  }

  private isNotFound(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 404;
  }

  private handleError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      this.errorMessage = 'You do not have access to data extraction agreement statistics.';
      this.markForCheck();
      return;
    }
    if (error instanceof HttpErrorResponse && error.status === 404) {
      this.errorMessage = 'Official data extraction agreement is not available yet.';
      this.markForCheck();
      return;
    }
    this.errorMessage = 'Could not load data extraction agreement statistics.';
    this.markForCheck();
  }

  private markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }
}
